import { Field, FieldInputProps, Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Text,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppMyAccountCard from "@/features/my-account/app-my-account-card";
import BusinessRegistrationCertificateRow, {
  BusinessRegistrationCertificateStatus,
} from "@/shared/components/business-registration-certificate-row";
import StatusBadge from "@/shared/components/StatusBadge";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useParseBusinessRegistrationCertificate from "@/shared/hooks/business-registration-certificate/useParseBusinessRegistrationCertificate";
import useGetBuyerBusinessLicense from "@/shared/hooks/business-license/useGetBuyerBusinessLicense";
import useUpdateBuyerBusinessLicense from "@/shared/hooks/business-license/useUpdateBuyerBusinessLicense";
import useGetBuyer from "@/shared/hooks/buyer/useGetBuyer";
import useUpdateBuyer from "@/shared/hooks/buyer/useUpdateBuyer";
import {
  BUSINESS_REGISTRATION_CERTIFICATE_ACCEPT,
  BUSINESS_REGISTRATION_NUMBER_PATTERN,
  formatBusinessRegistrationNumber,
  isAcceptableBusinessRegistrationCertificateType,
  MAX_BUSINESS_REGISTRATION_CERTIFICATE_FILE_SIZE,
} from "@/shared/constants/business-registration-certificate";

interface BusinessInfoFormValues {
  businessRegistrationNumber: string;
  userInputedCompanyName: string;
  taxInvoiceEmail: string;
}

// 자동 인식까지 끝났지만 아직 저장 버튼을 누르지 않은 파일.
// 저장 전에는 서버 URL이 없어 파일 보기에 원본 File이 필요하다.
interface PendingCertificate {
  unoFileId: number;
  file: File;
}

const validationSchema = Yup.object({
  businessRegistrationNumber: Yup.string()
    .required("사업자등록번호를 입력해 주세요.")
    .matches(BUSINESS_REGISTRATION_NUMBER_PATTERN, {
      message: "사업자등록번호 형식이 올바르지 않습니다. (예: 123-45-67890)",
      excludeEmptyString: true,
    }),
  userInputedCompanyName: Yup.string().required("사업자명을 입력해 주세요."),
  taxInvoiceEmail: Yup.string()
    .required("세금계산서 발행 이메일을 입력해 주세요.")
    .email("이메일 형식이 올바르지 않습니다."),
});

export default function BusinessInfoCard() {
  const { info } = useAppToasts({ id: "GLOBAL" });
  const { businessLicense, isPending: isBusinessLicenseLoading } =
    useGetBuyerBusinessLicense();
  const { parseBusinessRegistrationCertificate, isPending: isParsing } =
    useParseBusinessRegistrationCertificate();
  const { mutateAsync: updateBuyerBusinessLicense, isPending: isSaving } =
    useUpdateBuyerBusinessLicense();
  // 세금계산서 발행 이메일은 사업자등록증이 아니라 바이어 정보에 저장된다.
  const { buyer } = useGetBuyer();
  const { mutateAsync: updateBuyer, isPending: isSavingBuyer } =
    useUpdateBuyer();

  const fileInputRef = useRef<HTMLInputElement>(null);
  // 새 탭에 띄운 파일 URL — 탭이 살아 있어야 해서 언마운트할 때 정리한다.
  const objectUrlsRef = useRef<string[]>([]);
  const formikRef = useRef<FormikProps<BusinessInfoFormValues> | null>(null);

  const [pendingCertificate, setPendingCertificate] =
    useState<PendingCertificate | null>(null);
  // 인식 중에는 아직 등록이 확정되지 않아 파일명만 먼저 보여준다.
  const [parsingFileName, setParsingFileName] = useState<string | null>(null);
  // 파일 관련 안내는 Formik 에러와 별개로 관리한다.
  const [fileError, setFileError] = useState<string | null>(null);
  // 이번 화면에서 자동 인식을 거쳤는지 — 조회로 복원한 경우에는 인식 배지를 띄우지 않는다.
  const [isAutoRecognized, setIsAutoRecognized] = useState(false);
  const [hasMissingFields, setHasMissingFields] = useState(false);

  useEffect(() => {
    // 배열 자체는 교체되지 않고 push만 하므로 여기서 붙잡아 둔 참조로 정리해도 안전하다.
    const objectUrls = objectUrlsRef.current;
    return () => {
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    // 저장해 둔 사업자 정보와 세금계산서 이메일로 폼을 복원한다. 사용자가 이미 고치고 있는
    // 값을 덮어쓰지 않도록 폼이 깨끗할 때만 채운다.
    // 이메일은 사업자등록증과 별개로 저장되므로 등록증이 없어도 복원해야 한다.
    if (!formikRef.current || formikRef.current.dirty) {
      return;
    }
    formikRef.current.setValues({
      businessRegistrationNumber: formatBusinessRegistrationNumber(
        businessLicense?.businessRegistrationNumber ?? ""
      ),
      userInputedCompanyName: businessLicense?.businessName ?? "",
      taxInvoiceEmail: buyer?.taxInvoiceEmail ?? "",
    });
  }, [businessLicense, buyer]);

  const status: BusinessRegistrationCertificateStatus = isParsing
    ? "PARSING"
    : pendingCertificate
    ? "UNSAVED"
    : businessLicense
    ? "SAVED"
    : isBusinessLicenseLoading
    ? "LOADING"
    : "EMPTY";

  const registeredAt = useMemo(
    () =>
      businessLicense?.registeredAt
        ? new Date(businessLicense.registeredAt)
        : null,
    [businessLicense]
  );

  const openFilePicker = () => {
    if (isParsing || isSaving || isSavingBuyer) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleViewFile = () => {
    // 저장 전 파일은 아직 서버에 연결되지 않아 로컬 objectURL로 띄운다.
    if (pendingCertificate) {
      const url = URL.createObjectURL(pendingCertificate.file);
      objectUrlsRef.current.push(url);
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (businessLicense) {
      // 응답 url은 inline presigned URL이라 새 탭에서 바로 열린다.
      window.open(businessLicense.url, "_blank", "noopener,noreferrer");
    }
  };

  // 파일을 고르면 파싱 API를 호출해 사업자등록번호·사업자명을 채운다.
  const handleFileSelect = async (
    file: File,
    form: FormikProps<BusinessInfoFormValues>
  ) => {
    setFileError(null);
    setHasMissingFields(false);

    if (!isAcceptableBusinessRegistrationCertificateType(file)) {
      setFileError("PDF, JPG, JPEG, PNG 파일만 첨부할 수 있습니다.");
      return;
    }
    if (file.size > MAX_BUSINESS_REGISTRATION_CERTIFICATE_FILE_SIZE) {
      setFileError("파일 크기는 최대 10MB까지 첨부할 수 있습니다.");
      return;
    }

    // 새 파일을 고르면 이전 파싱 결과는 무효다.
    setPendingCertificate(null);
    setIsAutoRecognized(false);
    setParsingFileName(file.name);
    try {
      const { unoFileId, result } = await parseBusinessRegistrationCertificate(
        file
      );
      setHasMissingFields(result.missingRequiredFields.length > 0);
      setIsAutoRecognized(true);
      setPendingCertificate({ unoFileId, file });
      form.setValues((prev) => ({
        ...prev,
        // 응답 필드는 모두 nullable이라 못 읽은 항목은 빈 값으로 두고 사용자가 채운다.
        businessRegistrationNumber: result.businessRegistrationNumber ?? "",
        userInputedCompanyName: result.companyName ?? "",
        // 이미 입력한 이메일이 있으면 사용자가 고른 값을 우선한다.
        taxInvoiceEmail:
          prev.taxInvoiceEmail || (result.eTaxInvoiceEmail ?? ""),
      }));
    } catch {
      // 파싱에 실패하면 이전 등록 상태를 그대로 두고 안내만 노출한다.
      setFileError(
        "사업자등록증을 읽지 못했습니다. 다른 파일을 첨부해 주세요."
      );
    } finally {
      setParsingFileName(null);
    }
  };

  const isRegistered = status === "SAVED" || status === "UNSAVED";

  return (
    <AppMyAccountCard title={"사업자 정보"}>
      <Formik<BusinessInfoFormValues>
        initialValues={{
          businessRegistrationNumber: "",
          userInputedCompanyName: "",
          taxInvoiceEmail: "",
        }}
        validateOnChange={true}
        validationSchema={validationSchema}
        innerRef={formikRef}
        onSubmit={async (values, actions) => {
          // 파일 id는 저장 API의 필수값이다. 파일을 바꾸지 않고 사업자 정보만 고칠 때는
          // 이미 등록된 파일 id를 그대로 다시 보낸다.
          const fileId = pendingCertificate?.unoFileId ?? businessLicense?.id;
          if (!fileId) {
            return;
          }
          // 파일을 새로 붙였거나 사업자등록번호·사업자명을 고친 경우에만 등록 API를 태운다 —
          // 이메일만 고쳤을 때 같은 파일을 다시 등록해 등록일이 밀리지 않게 한다.
          // 저장된 번호는 복원할 때 하이픈 형식을 맞춰 넣으므로 같은 형식으로 비교한다.
          const hasCertificateInfoChanged =
            pendingCertificate !== null ||
            values.businessRegistrationNumber !==
              formatBusinessRegistrationNumber(
                businessLicense?.businessRegistrationNumber ?? ""
              ) ||
            values.userInputedCompanyName !==
              (businessLicense?.businessName ?? "");
          try {
            if (hasCertificateInfoChanged) {
              await updateBuyerBusinessLicense({
                businessRegistrationCertificateFileId: fileId,
                // 스펙상 nullable이라 빈 칸은 빈 문자열 대신 null로 보낸다.
                businessRegistrationNumber:
                  values.businessRegistrationNumber || null,
                businessName: values.userInputedCompanyName || null,
              });
            }
            // 세금계산서 이메일은 바이어 수정 API가 담당한다 — 값이 바뀐 경우에만
            // 그 필드 하나만 담아 보낸다(보낸 필드만 갱신되는 부분 수정이다).
            if (values.taxInvoiceEmail !== (buyer?.taxInvoiceEmail ?? "")) {
              await updateBuyer({
                taxInvoiceEmail: values.taxInvoiceEmail || null,
              });
            }
            setPendingCertificate(null);
            info("사업자 정보가 저장되었습니다.");
            actions.resetForm({ values });
          } catch {
            // 실패 토스트는 각 저장 훅의 onError가 띄운다.
          }
        }}
      >
        {(props) => (
          <Form>
            <input
              type={"file"}
              style={{ display: "none" }}
              ref={fileInputRef}
              accept={BUSINESS_REGISTRATION_CERTIFICATE_ACCEPT}
              onChange={(event) => {
                const file = event.target.files?.[0];
                // 같은 파일을 다시 선택해도 change 이벤트가 발생하도록 초기화한다.
                event.target.value = "";
                if (file) {
                  handleFileSelect(file, props);
                }
              }}
            />
            <Flex flexDirection={"column"} gap={"16px"}>
              <Flex flexDirection={"column"} gap={"12px"}>
                <BusinessRegistrationCertificateRow
                  status={status}
                  fileName={
                    parsingFileName ??
                    pendingCertificate?.file.name ??
                    businessLicense?.name ??
                    null
                  }
                  registeredAt={registeredAt}
                  onViewFile={handleViewFile}
                />
                <Text
                  fontSize={"12px"}
                  color={fileError ? "#CC1D1D" : "#8F8F8C"}
                >
                  {fileError ?? "PDF, JPG, JPEG, PNG · 최대 10MB"}
                </Text>
              </Flex>

              <Flex
                flexDirection={"column"}
                gap={"8px"}
                padding={"16px"}
                borderRadius={"4px"}
                border={"0.5px solid #EFEFED"}
                background={"#F7F8FA"}
              >
                <Flex
                  flexDirection={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  gap={"8px"}
                  // 배지 문구는 nowrap이라 버튼과 합치면 모바일 폭을 넘긴다. 넘칠 때는
                  // 배지를 다음 줄로 내려보낸다.
                  flexWrap={"wrap"}
                >
                  <AppButton
                    variant={"primary"}
                    size={"small"}
                    isDisabled={isParsing || isSaving || isSavingBuyer}
                    onClick={openFilePicker}
                  >
                    {isRegistered ? "사업자등록증 변경" : "사업자등록증 등록"}
                  </AppButton>
                  {isAutoRecognized ? (
                    hasMissingFields ? (
                      <StatusBadge
                        status={"일부 항목 확인 필요"}
                        colorVariant={"yellow"}
                      />
                    ) : (
                      <StatusBadge
                        status={"자동 인식 완료"}
                        colorVariant={"green"}
                      />
                    )
                  ) : (
                    // 조회로 복원만 된 상태에서는 이번 화면에서 인식한 게 없어 배지를 띄우지 않는다.
                    !isRegistered && (
                      <StatusBadge
                        status={"등록 후 자동 입력"}
                        colorVariant={"gray"}
                      />
                    )
                  )}
                </Flex>
                <Flex flexDirection={"column"} gap={"2px"}>
                  <Text fontSize={"12px"} color={"#6F6F6F"}>
                    사업자등록증을 등록하면 사업자등록번호와 사업자명을 자동으로
                    불러옵니다.
                  </Text>
                  {isAutoRecognized &&
                    (hasMissingFields ? (
                      <Text fontSize={"12px"} color={"#DE7000"}>
                        일부 항목을 자동으로 채우지 못했습니다. 값을 확인해
                        주세요.
                      </Text>
                    ) : (
                      <Text fontSize={"12px"} color={"#1272EF"}>
                        자동으로 입력된 정보를 확인하고 필요한 경우 수정해
                        주세요.
                      </Text>
                    ))}
                </Flex>

                <Grid
                  // 2열을 유지하면 모바일에서 한 열이 150px 남짓이 되어 사업자등록번호
                  // 플레이스홀더조차 잘린다. 모바일에서는 한 열로 쌓는다.
                  gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
                  columnGap={"16px"}
                  rowGap={"12px"}
                  paddingTop={"8px"}
                >
                  <Field name={"businessRegistrationNumber"}>
                    {({
                      field,
                      form,
                    }: {
                      field: FieldInputProps<string>;
                      form: FormikProps<BusinessInfoFormValues>;
                    }) => (
                      <FormControl
                        isRequired
                        isInvalid={
                          !!form.errors.businessRegistrationNumber &&
                          !!form.touched.businessRegistrationNumber
                        }
                      >
                        <FormLabel display={"flex"} m={"0"}>
                          <Text
                            color={"#6F6F6F"}
                            fontSize={"12px"}
                            fontWeight={"400"}
                          >
                            사업자등록번호
                          </Text>
                        </FormLabel>
                        <Input
                          {...field}
                          fontSize={"12px"}
                          minHeight={"0"}
                          height={"28px"}
                          // 사업자등록증을 등록해야 자동으로 채워지는 칸이라 그전에는 입력을 막는다.
                          isDisabled={!isRegistered}
                          background={isRegistered ? "#FFF" : "#EFEFED"}
                          inputMode={"numeric"}
                          maxLength={12}
                          placeholder={
                            isRegistered ? "123-45-67890" : undefined
                          }
                          onChange={(event) => {
                            form.setFieldValue(
                              field.name,
                              formatBusinessRegistrationNumber(
                                event.target.value
                              )
                            );
                          }}
                        />
                        {/* 좁은 폭에서 에러 문구가 두 줄로 감기면 고정 높이에 잘린다. */}
                        <Box minHeight={"16px"}>
                          <FormErrorMessage
                            m={"0"}
                            mt={"4px"}
                            fontSize={"12px"}
                          >
                            {form.errors.businessRegistrationNumber}
                          </FormErrorMessage>
                        </Box>
                      </FormControl>
                    )}
                  </Field>
                  <Field name={"userInputedCompanyName"}>
                    {({
                      field,
                      form,
                    }: {
                      field: FieldInputProps<string>;
                      form: FormikProps<BusinessInfoFormValues>;
                    }) => (
                      <FormControl
                        isRequired
                        isInvalid={
                          !!form.errors.userInputedCompanyName &&
                          !!form.touched.userInputedCompanyName
                        }
                      >
                        <FormLabel display={"flex"} m={"0"}>
                          <Text
                            color={"#6F6F6F"}
                            fontSize={"12px"}
                            fontWeight={"400"}
                          >
                            사업자명
                          </Text>
                        </FormLabel>
                        <Input
                          {...field}
                          fontSize={"12px"}
                          minHeight={"0"}
                          height={"28px"}
                          isDisabled={!isRegistered}
                          background={isRegistered ? "#FFF" : "#EFEFED"}
                          placeholder={
                            isRegistered
                              ? "사업자명을 입력해 주세요"
                              : undefined
                          }
                        />
                        <Box minHeight={"16px"}>
                          <FormErrorMessage
                            m={"0"}
                            mt={"4px"}
                            fontSize={"12px"}
                          >
                            {form.errors.userInputedCompanyName}
                          </FormErrorMessage>
                        </Box>
                      </FormControl>
                    )}
                  </Field>
                  <Field name={"taxInvoiceEmail"}>
                    {({
                      field,
                      form,
                    }: {
                      field: FieldInputProps<string>;
                      form: FormikProps<BusinessInfoFormValues>;
                    }) => (
                      <FormControl
                        isRequired
                        gridColumn={"1 / -1"}
                        isInvalid={
                          !!form.errors.taxInvoiceEmail &&
                          !!form.touched.taxInvoiceEmail
                        }
                      >
                        <FormLabel display={"flex"} m={"0"}>
                          <Text
                            color={"#6F6F6F"}
                            fontSize={"12px"}
                            fontWeight={"400"}
                          >
                            세금계산서 발행 이메일
                          </Text>
                        </FormLabel>
                        <Input
                          {...field}
                          type={"email"}
                          fontSize={"12px"}
                          minHeight={"0"}
                          height={"28px"}
                          background={"#FFF"}
                          placeholder={"tax@company.com"}
                        />
                        <Box minHeight={"16px"}>
                          {form.errors.taxInvoiceEmail &&
                          form.touched.taxInvoiceEmail ? (
                            <FormErrorMessage
                              m={"0"}
                              mt={"4px"}
                              fontSize={"12px"}
                            >
                              {form.errors.taxInvoiceEmail}
                            </FormErrorMessage>
                          ) : (
                            <Text fontSize={"12px"} color={"#8F8F8C"}>
                              멤버십 신청 시 세금계산서 수신 이메일로 기본값으로
                              사용됩니다.
                            </Text>
                          )}
                        </Box>
                      </FormControl>
                    )}
                  </Field>
                </Grid>
              </Flex>

              <Flex justifyContent={"flex-end"}>
                <AppButton
                  variant={"primary"}
                  size={"medium"}
                  type={"submit"}
                  isLoading={isSaving || isSavingBuyer}
                  // 새 파일을 골랐거나, 등록된 상태에서 값을 고쳤을 때만 저장할 게 있다.
                  isDisabled={
                    !isRegistered ||
                    (!pendingCertificate && !props.dirty) ||
                    !props.isValid ||
                    isParsing
                  }
                  disabledMessage={
                    isRegistered
                      ? undefined
                      : "사업자등록증을 먼저 등록해 주세요."
                  }
                >
                  사업자 정보 저장
                </AppButton>
              </Flex>
            </Flex>
          </Form>
        )}
      </Formik>
    </AppMyAccountCard>
  );
}
